import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Lightbulb, AlertTriangle } from 'lucide-react';
import { fetchQualityDistribution, fetchAnchorAnalysis } from '../api/backlinks';
import { cn, calculatePBNScore, trustLevelConfig, getQualityGrade } from '../lib/utils';

/**
 * PortfolioInsights - Collapsible analysis panel with 5 micro-visualizations.
 *
 * 6a. Domain Concentration Donut
 * 6b. Referring Domains per Linking Domain
 * 6c. Anchor Text Variation by Domain
 * 6d. Link Age Distribution
 * 6e. Domain Trust Distribution
 */

// Simple donut segment using CSS conic-gradient
function MiniDonut({ segments, size = 48 }) {
  if (!segments.length) return null;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  let cumulative = 0;
  const gradientParts = segments.map((seg) => {
    const start = (cumulative / total) * 360;
    cumulative += seg.value;
    const end = (cumulative / total) * 360;
    return `${seg.color} ${start}deg ${end}deg`;
  });

  return (
    <div
      className="rounded-full flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: `conic-gradient(${gradientParts.join(', ')})`,
      }}
    >
      <div
        className="rounded-full bg-card m-auto"
        style={{ width: size * 0.55, height: size * 0.55, marginTop: size * 0.225 }}
      />
    </div>
  );
}

// Horizontal bar for simple proportional display
function HBar({ value, max, color = 'bg-primary', label, sublabel }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 truncate text-muted-foreground" title={label}>{label}</span>
      <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right tabular-nums text-foreground font-medium">{value}</span>
      {sublabel && <span className="text-muted-foreground/60 text-[10px]">{sublabel}</span>}
    </div>
  );
}

function formatCompact(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

export default function PortfolioInsights({ projectId }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: distData } = useQuery({
    queryKey: ['quality-distribution', projectId],
    queryFn: () => fetchQualityDistribution(projectId),
    enabled: !!projectId,
    staleTime: 60000,
  });

  const { data: anchorData } = useQuery({
    queryKey: ['anchor-analysis', projectId],
    queryFn: () => fetchAnchorAnalysis(projectId),
    enabled: !!projectId && isExpanded,
    staleTime: 60000,
  });

  const backlinks = distData?.backlinks || [];
  const anchorAnalysis = anchorData?.analysis || [];

  // ---- Computed insights ----

  // 6a. Domain concentration
  const domainConcentration = useMemo(() => {
    const domainMap = new Map();
    for (const bl of backlinks) {
      let domain;
      try { domain = new URL(bl.backlink_url).hostname.replace(/^www\./, ''); } catch { domain = bl.backlink_url; }
      domainMap.set(domain, (domainMap.get(domain) || 0) + 1);
    }
    const sorted = [...domainMap.entries()].sort((a, b) => b[1] - a[1]);
    const top5 = sorted.slice(0, 5);
    const otherCount = sorted.slice(5).reduce((s, [, c]) => s + c, 0);
    return { top5, otherCount, totalDomains: domainMap.size };
  }, [backlinks]);

  // 6b. Top linking domains (with in-project count + global refs)
  const refDomainAnalysis = useMemo(() => {
    const domainMap = new Map();
    for (const bl of backlinks) {
      let domain;
      try { domain = new URL(bl.backlink_url).hostname.replace(/^www\./, ''); } catch { continue; }
      if (!domainMap.has(domain)) {
        domainMap.set(domain, {
          da: parseFloat(bl.domain_authority) || 0,
          refDomains: parseInt(bl.dfs_referring_domains) || 0,
          traffic: parseInt(bl.traffic) || 0,
          linksInProject: 0,
        });
      }
      domainMap.get(domain).linksInProject++;
    }
    return [...domainMap.entries()]
      .map(([domain, d]) => ({ domain, ...d }))
      .sort((a, b) => b.linksInProject - a.linksInProject || b.da - a.da)
      .slice(0, 10);
  }, [backlinks]);

  const pbnSuspects = refDomainAnalysis.filter(d => d.da >= 30 && d.refDomains < 10).length;

  // 6c. Anchor text variation flagged domains
  const anchorFlags = useMemo(() => {
    return anchorAnalysis.filter(a => a.diversity_ratio <= 0.2 && a.total_links >= 3);
  }, [anchorAnalysis]);

  // 6d. Link age distribution
  const ageDistribution = useMemo(() => {
    const now = Date.now();
    const fresh = { count: 0, label: 'Fresh (<30d)' };
    const established = { count: 0, label: 'Established (30-180d)' };
    const veteran = { count: 0, label: 'Veteran (180d+)' };

    for (const bl of backlinks) {
      if (!bl.created_at) continue;
      const days = Math.floor((now - new Date(bl.created_at).getTime()) / (1000 * 60 * 60 * 24));
      if (days < 30) fresh.count++;
      else if (days < 180) established.count++;
      else veteran.count++;
    }
    return [fresh, established, veteran];
  }, [backlinks]);

  const totalAge = ageDistribution.reduce((s, a) => s + a.count, 0);

  // 6e. Trust distribution
  const trustDistribution = useMemo(() => {
    const counts = { trusted: 0, caution: 0, suspicious: 0, unknown: 0 };
    const seen = new Set();

    for (const bl of backlinks) {
      let domain;
      try { domain = new URL(bl.backlink_url).hostname.replace(/^www\./, ''); } catch { continue; }
      if (seen.has(domain)) continue;
      seen.add(domain);

      const { trustLevel } = calculatePBNScore({
        da: parseFloat(bl.domain_authority) || 0,
        traffic: parseInt(bl.traffic) || 0,
        referringDomains: parseInt(bl.dfs_referring_domains) || 0,
        referringIps: parseInt(bl.dfs_referring_ips) || 0,
        backlinksCount: parseInt(bl.dfs_backlinks_count) || 0,
        spamScore: parseFloat(bl.spam_score) || 0,
        dfsSpamScore: bl.dfs_spam_score != null ? parseFloat(bl.dfs_spam_score) : null,
      });
      counts[trustLevel]++;
    }
    return counts;
  }, [backlinks]);

  const totalTrustDomains = Object.values(trustDistribution).reduce((s, c) => s + c, 0);
  const suspiciousPct = totalTrustDomains > 0 ? Math.round((trustDistribution.suspicious / totalTrustDomains) * 100) : 0;

  // Count total insights/callouts
  const insightCount = [
    domainConcentration.top5.length > 0 && domainConcentration.top5[0]?.[1] > backlinks.length * 0.3,
    pbnSuspects > 0,
    anchorFlags.length > 0,
    suspiciousPct > 10,
  ].filter(Boolean).length;

  if (backlinks.length === 0) return null;

  const donutColors = [
    'hsl(var(--primary))',
    'hsl(var(--success))',
    'hsl(var(--warning))',
    'hsl(var(--info))',
    'hsl(var(--destructive))',
    'hsl(var(--muted-foreground))',
  ];

  return (
    <div className="glow-card-filled rounded-xl overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-info" />
          <span className="text-sm font-medium text-foreground">Portfolio Insights</span>
          {insightCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-warning/20 text-warning">
              {insightCount}
            </span>
          )}
        </div>
        {isExpanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
        }
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-5">
          {/* 6a. Domain Concentration */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Domain Concentration
            </h4>
            <div className="flex items-start gap-4">
              <MiniDonut
                segments={[
                  ...domainConcentration.top5.map(([, count], i) => ({
                    value: count,
                    color: donutColors[i] || donutColors[5],
                  })),
                  ...(domainConcentration.otherCount > 0
                    ? [{ value: domainConcentration.otherCount, color: donutColors[5] }]
                    : []),
                ]}
              />
              <div className="flex-1 space-y-1">
                {domainConcentration.top5.map(([domain, count], i) => (
                  <div key={domain} className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: donutColors[i] }} />
                    <span className="truncate text-foreground flex-1" title={domain}>{domain}</span>
                    <span className="text-muted-foreground tabular-nums">{count}</span>
                  </div>
                ))}
                {domainConcentration.otherCount > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                    <span className="text-muted-foreground flex-1">Other ({domainConcentration.totalDomains - 5})</span>
                    <span className="text-muted-foreground tabular-nums">{domainConcentration.otherCount}</span>
                  </div>
                )}
              </div>
            </div>
            {domainConcentration.top5[0] && domainConcentration.top5[0][1] > backlinks.length * 0.3 && (
              <p className="text-xs text-warning mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {Math.round((domainConcentration.top5[0][1] / backlinks.length) * 100)}% of links from {domainConcentration.top5[0][0]} — consider diversifying
              </p>
            )}
          </div>

          {/* 6b. Top Linking Domains (dual-metric: in-project + global refs) */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Top Linking Domains
            </h4>
            <div className="space-y-1.5">
              {refDomainAnalysis.slice(0, 8).map(d => (
                <div key={d.domain} className="flex items-center gap-2 text-xs">
                  <span className="w-28 truncate text-foreground font-medium" title={d.domain}>{d.domain}</span>
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-muted-foreground">
                      <span className="text-foreground font-medium">{d.linksInProject}</span> in project
                    </span>
                    <span className="text-muted-foreground/50">|</span>
                    <span className="text-muted-foreground">
                      <span className="text-foreground font-medium">{formatCompact(d.refDomains)}</span> refs globally
                    </span>
                  </div>
                  <span className={cn(
                    'tabular-nums font-medium',
                    d.da >= 30 && d.refDomains < 10 ? 'text-destructive' : 'text-muted-foreground'
                  )}>
                    DA {Math.round(d.da)}
                  </span>
                </div>
              ))}
            </div>
            {pbnSuspects > 0 && (
              <p className="text-xs text-warning mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {pbnSuspects} domain{pbnSuspects > 1 ? 's have' : ' has'} DA&gt;30 but &lt;10 referring domains — possible PBN
              </p>
            )}
          </div>

          {/* 6c. Anchor Text Variation */}
          {anchorAnalysis.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Anchor Text Diversity
              </h4>
              <div className="space-y-1.5">
                {anchorAnalysis.slice(0, 8).map(a => (
                  <HBar
                    key={a.domain}
                    label={a.domain}
                    value={a.unique_anchors}
                    max={Math.max(...anchorAnalysis.slice(0, 8).map(x => x.total_links), 1)}
                    color={a.diversity_ratio <= 0.2 && a.total_links >= 3 ? 'bg-warning' : 'bg-success'}
                    sublabel={`${a.total_links} links`}
                  />
                ))}
              </div>
              {anchorFlags.length > 0 && (
                <p className="text-xs text-warning mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {anchorFlags.length} domain{anchorFlags.length > 1 ? 's use' : ' uses'} repetitive anchor text — unnatural pattern
                </p>
              )}
            </div>
          )}

          {/* 6d. Link Age Distribution */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Link Age Distribution
            </h4>
            <div className="flex h-4 rounded-full overflow-hidden bg-muted/30">
              {ageDistribution.map((age, i) => {
                const pct = totalAge > 0 ? (age.count / totalAge) * 100 : 0;
                if (pct === 0) return null;
                const colors = ['bg-info', 'bg-primary', 'bg-success'];
                return (
                  <div
                    key={i}
                    className={cn('h-full', colors[i])}
                    style={{ width: `${pct}%` }}
                    title={`${age.label}: ${age.count}`}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-1.5">
              {ageDistribution.map((age, i) => {
                const colors = ['text-info', 'text-primary', 'text-success'];
                const bgColors = ['bg-info', 'bg-primary', 'bg-success'];
                return (
                  <div key={i} className="flex items-center gap-1 text-[11px]">
                    <div className={cn('w-2 h-2 rounded-full', bgColors[i])} />
                    <span className={colors[i]}>{age.count}</span>
                    <span className="text-muted-foreground">{age.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 6e. Domain Trust Distribution */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Domain Trust Distribution
            </h4>
            <div className="flex h-4 rounded-full overflow-hidden bg-muted/30">
              {['trusted', 'caution', 'suspicious', 'unknown'].map(level => {
                const count = trustDistribution[level];
                const pct = totalTrustDomains > 0 ? (count / totalTrustDomains) * 100 : 0;
                if (pct === 0) return null;
                const bg = {
                  trusted: 'bg-success',
                  caution: 'bg-warning',
                  suspicious: 'bg-destructive',
                  unknown: 'bg-muted-foreground/40',
                };
                return (
                  <div
                    key={level}
                    className={cn('h-full', bg[level])}
                    style={{ width: `${pct}%` }}
                    title={`${trustLevelConfig[level].label}: ${count}`}
                  />
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-1.5">
              {['trusted', 'caution', 'suspicious', 'unknown'].map(level => {
                const count = trustDistribution[level];
                if (count === 0) return null;
                const config = trustLevelConfig[level];
                const bg = {
                  trusted: 'bg-success',
                  caution: 'bg-warning',
                  suspicious: 'bg-destructive',
                  unknown: 'bg-muted-foreground/40',
                };
                return (
                  <div key={level} className="flex items-center gap-1 text-[11px]">
                    <div className={cn('w-2 h-2 rounded-full', bg[level])} />
                    <span className={config.color}>{count}</span>
                    <span className="text-muted-foreground">{config.label}</span>
                  </div>
                );
              })}
            </div>
            {suspiciousPct > 10 && (
              <p className="text-xs text-warning mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {suspiciousPct}% of linking domains flagged as suspicious — review recommended
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
